import React from 'react';
import { ViewHint } from './ViewHint';
import type { Product } from '../../pages/CatalogPage';
import { sortByAvailability } from '../../utils/tableHelpers';
import type { Country } from '../../types';
import { EmptyState } from '../platform/EmptyState';

type HardwareTableSectionProps = {
  products: Product[];
  visibleCountries: Country[];
  includeHeader?: boolean;
  browseMode: boolean;
  selectedCountry: string | null;
  onDeviceClick: (product: Product) => void;
  countries: Country[];
}

// Helper function to render country availability (true, false, or "Coming Soon")
const renderCountryAvailability = (
  countries: Record<string, boolean | string>,
  countryCode: string
) => {
  const value = countries[countryCode];
  return (
    <td key={countryCode} className="p-4 text-center" style={{ width: '60px' }}>
      {value === true ? (
        <span className="text-green-600 font-semibold">✓</span>
      ) : value === 'Coming Soon' ? (
        <span className="text-[10px] italic text-muted-foreground">Soon</span>
      ) : value === 'N/A' ? (
        <span className="text-muted-foreground text-xs">—</span>
      ) : (
        <span className="text-red-500 font-semibold">✗</span>
      )}
    </td>
  );
};

export function HardwareTableSection({
  products,
  visibleCountries,
  includeHeader = false,
  browseMode,
  selectedCountry,
  onDeviceClick,
  countries,
}: HardwareTableSectionProps) {
  // Filter products that have availability in at least one of the shown countries
  const filteredProducts = products.filter(product => {
    if (browseMode) {
      return visibleCountries.some(country =>
        product.countries[country.code] === true ||
        product.countries[country.code] === 'Coming Soon'
      );
    } else {
      return selectedCountry && (
        product.countries[selectedCountry] === true ||
        product.countries[selectedCountry] === 'Coming Soon'
      );
    }
  });

  // Sort by country availability (most available first), then alphabetically
  const sortedProducts = sortByAvailability(filteredProducts);

  if (sortedProducts.length === 0) {
    return (
      <EmptyState message="Currently there is nothing to show here" />
    );
  }

  const columnWidth = visibleCountries.length >= 14 ? '45px' : '60px';

  // Show card view on mobile only if under 20 rows
  const showCardsOnMobile = sortedProducts.length < 20;

  return (
    <>
      {/* Mobile Card View */}
      <div className={showCardsOnMobile ? 'block md:hidden space-y-4' : 'hidden space-y-4'}>
        {sortedProducts.map((product, index) => (
          <div
            key={index}
            onClick={() => onDeviceClick(product)}
            className="bg-white border border-border rounded-lg p-4 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
          >
            <div className="font-semibold text-foreground text-base mb-3 pb-2 border-b border-border">
              {`${product.hardware_family || ''} ${product.model || ''} ${product.tag || ''}`.trim() ||
                ''}
            </div>
            <div className="grid grid-cols-3 gap-2">
              {browseMode
                ? visibleCountries.map(country => {
                    const value = product.countries[country.code];
                    return (
                      <div
                        key={country.code}
                        className="flex flex-col items-center justify-center p-2 bg-gray-50 rounded"
                      >
                        <span className="text-xs text-muted-foreground mb-1">{country.flag}</span>
                        <span className="text-xs font-medium text-muted-foreground">
                          {country.code}
                        </span>
                        <span className="mt-1">
                          {value === true ? (
                            <span className="text-green-600 font-semibold">✓</span>
                          ) : value === 'Coming Soon' ? (
                            <span className="text-xs italic text-muted-foreground">Soon</span>
                          ) : value === 'N/A' ? (
                            <span className="text-muted-foreground text-xs">—</span>
                          ) : (
                            <span className="text-red-500 font-semibold">✗</span>
                          )}
                        </span>
                      </div>
                    );
                  })
                : selectedCountry && (
                    <div className="col-span-3 flex items-center justify-between p-3 bg-gray-50 rounded">
                      <span className="text-sm font-medium text-muted-foreground">
                        {selectedCountry}
                      </span>
                      {product.development && (
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            product.development === 'Ready'
                              ? 'bg-green-100 text-green-800'
                              : product.development === 'In Pilot'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {product.development}
                        </span>
                      )}
                    </div>
                  )}
            </div>
          </div>
        ))}
      </div>

      {/* Table View (shows on mobile for 20+ rows, always on desktop) */}
      <div
        className={
          showCardsOnMobile
            ? 'hidden md:block bg-card border border-border rounded-lg overflow-x-auto'
            : 'block bg-card border border-border rounded-lg overflow-x-auto'
        }
      >
        {/* Table */}
        <table className="w-full min-w-[600px]" style={{ tableLayout: 'fixed' }}>
          {includeHeader && (
            <thead className="bg-white dark:bg-gray-900 sticky top-0 z-10">
              <tr className="h-20 border-b border-border">
                <th
                  className="text-left px-4 py-2 text-sm font-medium text-foreground bg-white dark:bg-gray-900"
                  style={{ width: '300px' }}
                >
                  Hardware
                </th>
                {browseMode ? (
                  visibleCountries.map(country => (
                    <th
                      key={country.code}
                      className="text-center px-1 py-2 font-medium text-foreground bg-white dark:bg-gray-900 relative group cursor-default select-none"
                      style={{ width: columnWidth }}
                    >
                      <div className="flex flex-col items-center justify-center h-full">
                        {visibleCountries.length >= 14 ? (
                          <span className="text-xs select-none whitespace-nowrap transform -rotate-45 origin-center">
                            {country.code}
                          </span>
                        ) : (
                          <div className="flex flex-col items-center justify-center h-full gap-1">
                            <span className="text-sm select-none">{country.flag}</span>
                            <span className="text-xs select-none whitespace-nowrap">
                              {country.code}
                            </span>
                          </div>
                        )}
                      </div>
                    </th>
                  ))
                ) : (
                  selectedCountry && (
                    <th
                      className="text-center px-1 py-2 font-medium text-foreground bg-white dark:bg-gray-900 relative group cursor-default select-none"
                      style={{ width: columnWidth }}
                    >
                      <div className="flex flex-col items-center justify-center h-full">
                        <div className="flex flex-col items-center justify-center h-full gap-1">
                          <span className="text-sm select-none">
                            {countries.find(c => c.code === selectedCountry)?.flag}
                          </span>
                          <span className="text-xs select-none whitespace-nowrap">
                            {selectedCountry}
                          </span>
                        </div>
                      </div>
                    </th>
                  )
                )}
              </tr>
            </thead>
          )}
          <tbody>
            {sortedProducts.map((product, index) => {
              return (
                <tr
                  key={index}
                  className="border-b border-border hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:shadow-sm cursor-pointer group transition-colors"
                  data-product-name={product.solution}
                  onClick={() => onDeviceClick(product)}
                >
                  <td className="p-4 align-top" style={{ width: '300px' }}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-medium text-foreground text-sm leading-tight">
                          {`${product.hardware_family || ''} ${product.model || ''} ${product.tag || ''}`.trim()}
                        </div>
                        {product.solution_description && (
                          <div
                            className="text-muted-foreground italic pr-2"
                            style={{ fontSize: '10px', lineHeight: 1.375, marginTop: '4px' }}
                          >
                            {product.solution_description}
                          </div>
                        )}
                      </div>
                      <ViewHint label="View Details" />
                    </div>
                  </td>
                  {browseMode ? (
                    // Show country availability for browse mode - only visible countries
                    visibleCountries.map(country => {
                      const value = product.countries[country.code];
                      return (
                        <td
                          key={country.code}
                          className="p-4 text-center"
                          style={{ width: columnWidth }}
                        >
                          {value === true ? (
                            <span className="text-green-600 font-semibold">✓</span>
                          ) : value === 'Coming Soon' ? (
                            <span className="text-[10px] italic text-muted-foreground">Soon</span>
                          ) : value === 'N/A' ? (
                            <span className="text-muted-foreground text-xs">—</span>
                          ) : (
                            <span className="text-red-500 font-semibold">✗</span>
                          )}
                        </td>
                      );
                    })
                  ) : (
                    // Show selected country column for location-filtered mode
                    <>
                      <td className="p-3 text-sm" style={{ width: '120px' }}>
                        {product.development && (
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              product.development === 'Ready'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                : product.development === 'In Pilot'
                                  ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                                  : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
                            }`}
                          >
                            {product.development}
                          </span>
                        )}
                      </td>
                      {selectedCountry &&
                        renderCountryAvailability(product.countries, selectedCountry)}
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};
